import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Multi-select dropdown with search and checkboxes.
 *
 * Props:
 *  - options: [{ key, label }]
 *  - value: string[] (selected keys)
 *  - onChange: (newValue) => void
 *  - placeholder: string
 *  - searchable: bool
 *  - dataTestId: string
 */
export default function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Sélectionner...",
  searchable = true,
  dataTestId = "multiselect",
  emptyLabel = "Aucune option",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const toggle = (k) => {
    const next = value.includes(k) ? value.filter((v) => v !== k) : [...value, k];
    onChange(next);
  };
  const clearAll = (e) => { e.stopPropagation(); onChange([]); setQuery(""); };
  const selectAllFiltered = () => onChange(Array.from(new Set([...value, ...filtered.map((o) => o.key)])));

  const filtered = searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()) || o.key.toLowerCase().includes(query.toLowerCase()))
    : options;

  const count = value.length;
  const summary = count === 0
    ? placeholder
    : count === 1
      ? options.find((o) => o.key === value[0])?.label || `1 sélectionné`
      : `${count} sélectionnés`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-9 px-3 text-sm bg-white border border-gray-300 hover:border-gray-400 flex items-center gap-2 transition-colors text-left"
          data-testid={`${dataTestId}-trigger`}
        >
          <span className={`flex-1 truncate ${count === 0 ? "text-gray-400" : "text-[#3F3F3F] font-medium"}`}>
            {summary}
          </span>
          {count > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearAll}
              onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }}
              className="p-0.5 hover:bg-red-50 text-red-500 shrink-0 cursor-pointer"
              title="Tout désélectionner"
              data-testid={`${dataTestId}-clear`}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-none" align="start" sideOffset={4}>
        {searchable && (
          <div className="p-2 border-b border-gray-100">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher..."
              className="rounded-none h-8 text-xs"
              autoFocus
              data-testid={`${dataTestId}-search`}
            />
          </div>
        )}
        {filtered.length > 0 ? (
          <>
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-100 bg-[#FAFAFA]">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                {count} / {options.length} sélectionnés
              </span>
              <button
                type="button"
                onClick={selectAllFiltered}
                className="text-[10px] text-[#0069B4] hover:underline font-semibold"
                data-testid={`${dataTestId}-select-all`}
              >
                Tout cocher ({filtered.length})
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.map((opt) => {
                const checked = value.includes(opt.key);
                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer hover:bg-[#F0E9F3] transition-colors ${checked ? "bg-[#F0E9F3]/50" : ""}`}
                    data-testid={`${dataTestId}-option-${opt.key}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(opt.key)} className="mt-0.5 shrink-0" />
                    <span className={`text-xs flex-1 ${checked ? "text-[#662483] font-semibold" : "text-gray-700"}`}>
                      {opt.label}
                    </span>
                    {checked && <Check className="w-3 h-3 text-[#662483] mt-0.5 shrink-0" />}
                  </label>
                );
              })}
            </div>
          </>
        ) : (
          <div className="py-6 text-center text-xs text-gray-400">{emptyLabel}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
